/**
 * Offline synchronization Zustand store
 * Manages offline sync queue status and network connectivity state
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface SyncStatus {
  isOnline: boolean
  isSyncing: boolean
  lastSyncAt: number | null
  pendingCount: number
  failedCount: number
  conflictCount: number
  syncProgress: number
}

interface OfflineSyncStore extends SyncStatus {
  setOnline: (online: boolean) => void
  setSyncing: (syncing: boolean) => void
  setLastSyncAt: (timestamp: number | null) => void
  setPendingCount: (count: number) => void
  setFailedCount: (count: number) => void
  setConflictCount: (count: number) => void
  setSyncProgress: (progress: number) => void
  reset: () => void
}

const initialState: SyncStatus = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isSyncing: false,
  lastSyncAt: null,
  pendingCount: 0,
  failedCount: 0,
  conflictCount: 0,
  syncProgress: 0,
}

export const useOfflineSyncStore = create<OfflineSyncStore>()(
  persist(
    (set) => ({
      ...initialState,
      setOnline: (online) => set({ isOnline: online }),
      setSyncing: (syncing) => set({ isSyncing: syncing }),
      setLastSyncAt: (timestamp) => set({ lastSyncAt: timestamp }),
      setPendingCount: (count) => set({ pendingCount: count }),
      setFailedCount: (count) => set({ failedCount: count }),
      setConflictCount: (count) => set({ conflictCount: count }),
      setSyncProgress: (progress) => set({ syncProgress: Math.max(0, Math.min(100, progress)) }),
      reset: () => set(initialState),
    }),
    {
      name: 'offline-sync-storage',
      partialize: (state) => ({
        lastSyncAt: state.lastSyncAt,
        pendingCount: state.pendingCount,
        failedCount: state.failedCount,
        conflictCount: state.conflictCount,
      }),
    }
  )
)

